import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpen, Sparkles, PenTool, Users, Map, Stethoscope, Eye, Wand2,
  Volume2, FileText, Save, ChevronLeft, ChevronRight, Menu, X,
  Loader2, Play, Pause, Square, RotateCcw, Download, Upload, Zap,
  HelpCircle, Settings, LogOut, Crown, Star, ArrowLeft, Check,
  BarChart3, Target, RefreshCw, Lightbulb, Edit3, Type,
  Search, Sun, Moon, Flame, ChevronUp, ChevronDown
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger
} from "../components/ui/sheet";
import { toast } from "sonner";
import ShweebumCat from "../components/ShweebumCat";
import { useAuth } from "../components/Auth/AuthProvider";
import { API } from "../lib/api";
import ShweebumScribe from "../components/ShweebumScribe";

// Free tools available in Freebum
const FREE_TOOLS = [
  { id: "write", label: "Write", icon: PenTool, description: "Write your story freely", color: "cyan" },
  { id: "outline", label: "Story Outline", icon: Map, description: "Plan your chapters", color: "violet" },
  { id: "characters", label: "Characters", icon: Users, description: "Build your cast", color: "pink" },
  { id: "doctor", label: "Story Doctor", icon: Stethoscope, description: "Find plot holes", color: "emerald" },
  { id: "insights", label: "Live Insights", icon: BarChart3, description: "Real-time writing stats", color: "teal" },
  { id: "search", label: "Smart Search", icon: Search, description: "Search your manuscript", color: "blue" },
  { id: "analysis", label: "Analysis", icon: Eye, description: "Readability scores", color: "amber" },
  { id: "polish", label: "Polish", icon: Wand2, description: "Improve your prose", color: "blue" },
  { id: "voice", label: "Listen", icon: Volume2, description: "Listen to your writing", color: "teal" },
];

// Writing polish tools (free)
const POLISH_TOOLS = [
  { id: "show_dont_tell", label: "Show Don't Tell", description: "Convert telling to showing" },
  { id: "sensory", label: "Sensory Expansion", description: "Add sensory details" },
  { id: "humanizer", label: "Humanizer", description: "Make prose more natural" },
  { id: "dialogue", label: "Dialogue Polish", description: "Improve conversations" },
];

const FreebumWritingPage = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Core state
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  
  // UI state
  const [activeTool, setActiveTool] = useState("write");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Chapter state
  const [currentChapter, setCurrentChapter] = useState(1);
  const [chapters, setChapters] = useState([]);
  
  // Tool-specific state
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [polishTool, setPolishTool] = useState(null);
  const [polishing, setPolishing] = useState(false);
  const [polishResult, setPolishResult] = useState(null);
  const [selectedText, setSelectedText] = useState("");
  
  // Voice state
  const [isPlaying, setIsPlaying] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState(null);
  
  // Limits tracking
  const [sparkUsed, setSparkUsed] = useState(0);
  const SPARK_LIMIT = 10;
  
  // Shweebum Scribe state
  const [showScribe, setShowScribe] = useState(true);
  
  const contentRef = useRef(null);
  const autoSaveTimer = useRef(null);
  const typingPauseTimer = useRef(null);

  // ===== NEW FEATURES STATE =====
  // Focus Mode (Kindle Mode)
  const [focusMode, setFocusMode] = useState(false);
  
  // Wow Factor - typing pause feedback
  const [wowMessage, setWowMessage] = useState(null);
  const wowVisible = useRef(false);
  
  // Live Writing Insights (computed on-the-fly)
  const [overusedWords, setOverusedWords] = useState([]);
  const [overusedIdx, setOverusedIdx] = useState(0);

  // Smart Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOccurrences, setSearchOccurrences] = useState([]);
  const [searchMatchIdx, setSearchMatchIdx] = useState(0);
  const [showSearch, setShowSearch] = useState(false);

  // Writing Streak
  const [streak, setStreak] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    
    // Check if user should be on Freebum
    const userTier = user.subscription?.toLowerCase() || 'free';
    const paidTiers = ['storyteller', 'plus', 'pro', 'author', 'premium', 'family'];
    if (paidTiers.includes(userTier)) {
      // Paid user - redirect to regular writing page
      navigate(`/write/${id}`);
      return;
    }
    
    if (id) {
      fetchProject();
    }
    
    // Initialize speech synthesis
    if (window.speechSynthesis) {
      setSpeechSynthesis(window.speechSynthesis);
    }

    // Writing Streak tracking
    const today = new Date().toDateString();
    const lastDate = localStorage.getItem('shweebum_last_write_date');
    const currentStreak = parseInt(localStorage.getItem('shweebum_write_streak') || '0');
    if (lastDate === today) {
      setStreak(currentStreak);
    } else if (lastDate && new Date(today) - new Date(lastDate) <= 86400000) {
      const newStreak = currentStreak + 1;
      localStorage.setItem('shweebum_write_streak', String(newStreak));
      localStorage.setItem('shweebum_last_write_date', today);
      setStreak(newStreak);
    } else {
      localStorage.setItem('shweebum_write_streak', '1');
      localStorage.setItem('shweebum_last_write_date', today);
      setStreak(1);
    }
    
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
      if (typingPauseTimer.current) clearTimeout(typingPauseTimer.current);
      if (speechSynthesis) speechSynthesis.cancel();
    };
  }, [user, id, navigate]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/project/${id}`);
      setProject(res.data);
      setContent(res.data.content || "");
      
      // Parse chapters from content
      const chapterMatches = (res.data.content || "").match(/---\s*CHAPTER\s+\d+/gi) || [];
      const chapterList = chapterMatches.map((_, idx) => ({
        number: idx + 1,
        title: res.data.chapter_outline?.[idx]?.title || `Chapter ${idx + 1}`
      }));
      setChapters(chapterList.length > 0 ? chapterList : [{ number: 1, title: "Chapter 1" }]);
      
      // Get usage
      const usageRes = await axios.get(`${API}/usage/${user.id}`);
      setSparkUsed(usageRes.data?.chapters_generated_this_month || 0);
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
    } finally {
      setLoading(false);
    }
  };

  const handleContentChange = (newContent) => {
    setContent(newContent);
    setHasChanges(true);
    
    // Auto-save after 3 seconds of no typing
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveContent(newContent);
    }, 3000);

    // Wow Factor - typing pause detection
    if (typingPauseTimer.current) clearTimeout(typingPauseTimer.current);
    typingPauseTimer.current = setTimeout(() => {
      if (!wowVisible.current && newContent.length > 100) {
        triggerWowFeedback(newContent);
      }
    }, 1800);

    // Live overused word detection
    detectOverusedWords(newContent);
  };

  // Wow Factor: local insights when writer pauses
  const triggerWowFeedback = (text) => {
    const words = text.toLowerCase().split(/\s+/);
    const wordFreq = {};
    words.forEach(w => { const clean = w.replace(/[^a-z]/g, ''); if (clean.length > 3) wordFreq[clean] = (wordFreq[clean] || 0) + 1; });
    
    const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 5);
    const avgLen = sentences.length > 0 ? Math.round(words.length / sentences.length) : 0;
    const overused = Object.entries(wordFreq).filter(([, c]) => c >= 5).sort((a, b) => b[1] - a[1]);
    const lastSentence = sentences[sentences.length - 1] || '';
    const dialogueCount = (text.match(/["']/g) || []).length;
    
    const messages = [];
    if (overused.length > 0) messages.push(`You've used "${overused[0][0]}" ${overused[0][1]} times — try a synonym?`);
    if (lastSentence.split(/\s+/).length > 30) messages.push("That last sentence is quite long — consider splitting it for more punch.");
    if (avgLen < 8 && sentences.length > 5) messages.push("Nice short sentences — great for pace and tension!");
    if (avgLen > 20) messages.push("Your sentences are flowing. Great for an immersive narrative mood.");
    if (dialogueCount > 4) messages.push("Lots of dialogue — your characters feel alive!");
    if (words.length > 0 && words.length % 100 === 0) messages.push(`${words.length} words written. You're on a roll!`);
    
    const genericMessages = [
      "Keep going — the story is finding its shape.",
      "You're building something real here.",
      "Great flow in this section!",
      "This moment feels authentic.",
    ];
    
    const pool = messages.length > 0 ? messages : genericMessages;
    const msg = pool[Math.floor(Math.random() * pool.length)];
    
    wowVisible.current = true;
    setWowMessage(msg);
    setTimeout(() => { setWowMessage(null); wowVisible.current = false; }, 4000);
  };

  // Detect overused words
  const detectOverusedWords = (text) => {
    if (text.length < 50) { setOverusedWords([]); return; }
    const words = text.toLowerCase().split(/\s+/);
    const freq = {};
    const IGNORE = new Set(['the','a','an','and','or','but','in','on','at','to','for','of','with','is','was','are','were','he','she','they','it','i','we','you','this','that','have','had','has','will','be','been','do','did','my','his','her','their','our','said','then','when','from','up','out','not','all','as','if','so']);
    words.forEach(w => { 
      const clean = w.replace(/[^a-z]/g, ''); 
      if (clean.length > 3 && !IGNORE.has(clean)) freq[clean] = (freq[clean] || 0) + 1; 
    });
    const overused = Object.entries(freq).filter(([, c]) => c >= 4).sort((a, b) => b[1] - a[1]).slice(0, 8);
    setOverusedWords(overused.map(([word, count]) => ({ word, count })));
  };

  // Smart Search
  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) { setSearchOccurrences([]); return; }
    const matches = [];
    const lower = content.toLowerCase();
    const q = query.toLowerCase();
    let idx = 0;
    while ((idx = lower.indexOf(q, idx)) !== -1) {
      const start = Math.max(0, idx - 40);
      const end = Math.min(content.length, idx + query.length + 40);
      matches.push({ pos: idx, snippet: '...' + content.slice(start, end).replace(/\n/g, ' ') + '...' });
      idx += q.length;
    }
    setSearchOccurrences(matches);
    setSearchMatchIdx(0);
  };

  const saveContent = async (contentToSave = content) => {
    if (!project || saving) return;
    
    setSaving(true);
    try {
      await axios.patch(`${API}/project/${id}`, {
        content: contentToSave
      });
      setHasChanges(false);
      toast.success("Saved!", { duration: 1500 });
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection()?.toString() || "";
    if (selection.length > 10) {
      setSelectedText(selection);
    }
  };

  // Quick Voice (Browser TTS) - FREE
  const handleQuickVoice = () => {
    if (!speechSynthesis) {
      toast.error("Your browser doesn't support text-to-speech");
      return;
    }
    
    if (isPlaying) {
      speechSynthesis.cancel();
      setIsPlaying(false);
      return;
    }
    
    const textToSpeak = selectedText || content;
    if (!textToSpeak) {
      toast.error("No text to read");
      return;
    }
    
    const utterance = new SpeechSynthesisUtterance(textToSpeak.slice(0, 5000));
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.onend = () => setIsPlaying(false);
    utterance.onerror = () => setIsPlaying(false);
    
    speechSynthesis.speak(utterance);
    setIsPlaying(true);
    toast.success("Playing audio...");
  };

  // Story Doctor Analysis - FREE
  const handleStoryDoctor = async () => {
    if (!content || content.length < 100) {
      toast.error("Need more content to analyze");
      return;
    }
    
    setAnalyzing(true);
    setAnalysisResult(null);
    
    try {
      const res = await axios.post(`${API}/story-doctor/analyze`, {
        project_id: id,
        content: content.slice(0, 15000),
        analysis_type: "quick"
      });
      setAnalysisResult(res.data);
      setActiveTool("doctor");
      toast.success("Analysis complete!");
    } catch (error) {
      toast.error("Analysis failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  // Manuscript Analysis - FREE
  const handleManuscriptAnalysis = async () => {
    if (!content || content.length < 100) {
      toast.error("Need more content to analyze");
      return;
    }
    
    setAnalyzing(true);
    
    try {
      // Simple client-side analysis
      const words = content.split(/\s+/).filter(w => w.length > 0);
      const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
      const paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 0);
      
      const avgWordsPerSentence = words.length / Math.max(sentences.length, 1);
      const avgSentencesPerParagraph = sentences.length / Math.max(paragraphs.length, 1);
      
      // Passive voice detection (simple)
      const passivePatterns = /\b(was|were|been|being)\s+\w+ed\b/gi;
      const passiveCount = (content.match(passivePatterns) || []).length;
      const passivePercent = (passiveCount / sentences.length * 100).toFixed(1);
      
      // Adverb detection
      const adverbCount = (content.match(/\b\w+ly\b/gi) || []).length;
      
      setAnalysisResult({
        type: "manuscript",
        wordCount: words.length,
        sentenceCount: sentences.length,
        paragraphCount: paragraphs.length,
        avgWordsPerSentence: avgWordsPerSentence.toFixed(1),
        avgSentencesPerParagraph: avgSentencesPerParagraph.toFixed(1),
        passiveVoicePercent: passivePercent,
        adverbCount,
        readabilityScore: avgWordsPerSentence < 15 ? "Easy" : avgWordsPerSentence < 20 ? "Medium" : "Complex"
      });
      
      setActiveTool("analysis");
      toast.success("Analysis complete!");
    } catch (error) {
      toast.error("Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  // Writing Polish - FREE
  const handlePolish = async (tool) => {
    const textToPolish = selectedText || content;
    if (!textToPolish || textToPolish.length < 20) {
      toast.error("Select some text or write more content first");
      return;
    }
    
    setPolishing(true);
    setPolishTool(tool);
    setPolishResult(null);
    
    try {
      const res = await axios.post(`${API}/writing-tools/polish`, {
        project_id: id,
        text: textToPolish.slice(0, 3000),
        tool: tool.id,
        style: "natural"
      });
      setPolishResult(res.data);
      toast.success("Polish complete!");
    } catch (error) {
      toast.error("Polish failed: " + (error.response?.data?.detail || error.message));
    } finally {
      setPolishing(false);
    }
  };

  const applyPolishResult = () => {
    if (!polishResult?.polished_text) return;
    
    if (selectedText) {
      const newContent = content.replace(selectedText, polishResult.polished_text);
      setContent(newContent);
      handleContentChange(newContent);
    } else {
      setContent(polishResult.polished_text);
      handleContentChange(polishResult.polished_text);
    }
    
    setPolishResult(null);
    setSelectedText("");
    toast.success("Applied!");
  };

  // Export - FREE for TXT/EPUB
  const handleExport = (format) => {
    if (format === 'txt') {
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project?.title || 'story'}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded as TXT!");
    } else if (format === 'pdf') {
      toast.error("PDF export requires Shweebum Plus. Upgrade to unlock!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto mb-3" />
          <p className="text-slate-400">Loading your story...</p>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400">Project not found</p>
          <Link to="/freebum">
            <Button className="mt-4">Back to Freebum</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810] flex flex-col">
      {/* Header */}
      <header className="bg-[#0A0E17]/95 backdrop-blur-md border-b border-cyan-500/20 sticky top-0 z-40">
        <div className="flex items-center justify-between px-3 py-2">
          {/* Left */}
          <div className="flex items-center gap-2">
            <Link to="/freebum">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-white font-medium text-sm truncate max-w-[150px] sm:max-w-[250px]">
                {project.title}
              </h1>
              <div className="flex items-center gap-2">
                <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30 text-[10px]">
                  Freebum
                </Badge>
                {hasChanges && (
                  <span className="text-[10px] text-amber-400">Unsaved</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Right */}
          <div className="flex items-center gap-2">
            {/* Writing Streak */}
            {streak > 0 && (
              <div className="hidden sm:flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded-full border border-amber-500/20" data-testid="writing-streak">
                <Flame className="w-3 h-3" />
                <span>{streak}d</span>
              </div>
            )}
            
            {/* Focus Mode Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFocusMode(!focusMode)}
              className={`text-slate-300 hover:text-white ${focusMode ? 'text-amber-300 bg-amber-500/10' : ''}`}
              title={focusMode ? "Exit Focus Mode" : "Enter Focus Mode (Kindle)"}
              data-testid="focus-mode-btn"
            >
              {focusMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>

            {/* Smart Search Toggle */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSearch(!showSearch)}
              className={`text-slate-300 hover:text-white ${showSearch ? 'text-cyan-300 bg-cyan-500/10' : ''}`}
              title="Smart Search"
              data-testid="smart-search-btn"
            >
              <Search className="w-4 h-4" />
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => saveContent()}
              disabled={saving || !hasChanges}
              className="text-slate-300 hover:text-white"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </Button>
            
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white md:hidden">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#0A0E17] border-slate-700 w-72">
                <SheetHeader>
                  <SheetTitle className="text-white">Free Tools</SheetTitle>
                </SheetHeader>
                <div className="mt-4 space-y-2">
                  {FREE_TOOLS.map(tool => (
                    <button
                      key={tool.id}
                      onClick={() => { setActiveTool(tool.id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all ${
                        activeTool === tool.id 
                          ? `bg-${tool.color}-500/20 border border-${tool.color}-500/40 text-${tool.color}-300`
                          : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                      }`}
                    >
                      <tool.icon className="w-4 h-4" />
                      <span className="text-sm">{tool.label}</span>
                    </button>
                  ))}
                </div>
                
                {/* Usage */}
                <div className="mt-6 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
                  <p className="text-xs text-slate-400 mb-2">Spark Chapters Used</p>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white">{sparkUsed} / {SPARK_LIMIT}</span>
                    {sparkUsed >= SPARK_LIMIT && (
                      <Badge className="bg-red-500/20 text-red-400 text-[10px]">Limit reached</Badge>
                    )}
                  </div>
                  <Progress value={(sparkUsed / SPARK_LIMIT) * 100} className="h-1.5" />
                </div>
                
                {/* Upgrade CTA */}
                <Link to="/pricing" className="block mt-4">
                  <Button className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400">
                    <Zap className="w-4 h-4 mr-2" />
                    Upgrade for More
                  </Button>
                </Link>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        
        {/* Tool tabs for desktop */}
        <div className="hidden md:flex items-center gap-1 px-3 pb-2 overflow-x-auto">
          {FREE_TOOLS.map(tool => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                activeTool === tool.id 
                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
            >
              <tool.icon className="w-3.5 h-3.5" />
              {tool.label}
            </button>
          ))}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex">
        {/* Desktop Sidebar */}
        <aside className="hidden md:flex flex-col w-48 bg-[#0A0E17] border-r border-slate-700/50 p-3">
          <div className="space-y-1.5">
            {FREE_TOOLS.map(tool => (
              <button
                key={tool.id}
                onClick={() => setActiveTool(tool.id)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm transition-all ${
                  activeTool === tool.id 
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`}
              >
                <tool.icon className="w-4 h-4 shrink-0" />
                <span className="truncate">{tool.label}</span>
              </button>
            ))}
          </div>
          
          {/* Usage */}
          <div className="mt-auto pt-4">
            <div className="p-2.5 bg-slate-900/50 rounded-lg border border-slate-700/50">
              <p className="text-[10px] text-slate-500 mb-1">Spark Chapters</p>
              <div className="flex items-center justify-between">
                <span className="text-xs text-white font-medium">{sparkUsed}/{SPARK_LIMIT}</span>
              </div>
              <Progress value={(sparkUsed / SPARK_LIMIT) * 100} className="h-1 mt-1.5" />
            </div>
            
            <Link to="/pricing" className="block mt-2">
              <Button size="sm" className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-xs">
                <Crown className="w-3 h-3 mr-1" />
                Upgrade
              </Button>
            </Link>
          </div>
        </aside>

        {/* Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* WRITE TOOL */}
          {activeTool === "write" && (
            <div className={`flex-1 flex flex-col p-4 transition-colors duration-500 ${focusMode ? 'bg-[#F4ECD8]' : ''}`}>
              {/* Smart Search Bar */}
              <AnimatePresence>
                {showSearch && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="mb-3 overflow-hidden"
                  >
                    <div className={`flex items-center gap-2 p-2 rounded-lg border ${focusMode ? 'bg-amber-50 border-amber-200' : 'bg-slate-800/50 border-slate-700/50'}`}>
                      <Search className={`w-4 h-4 shrink-0 ${focusMode ? 'text-slate-500' : 'text-cyan-400'}`} />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search in your manuscript..."
                        className={`flex-1 bg-transparent outline-none text-sm ${focusMode ? 'text-slate-800 placeholder:text-slate-400' : 'text-white placeholder:text-slate-500'}`}
                        data-testid="search-input"
                      />
                      {searchOccurrences.length > 0 && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-cyan-400">{searchMatchIdx + 1}/{searchOccurrences.length}</span>
                          <button onClick={() => setSearchMatchIdx(i => Math.max(0, i - 1))} className="p-1 text-slate-400 hover:text-white"><ChevronUp className="w-3 h-3" /></button>
                          <button onClick={() => setSearchMatchIdx(i => Math.min(searchOccurrences.length - 1, i + 1))} className="p-1 text-slate-400 hover:text-white"><ChevronDown className="w-3 h-3" /></button>
                        </div>
                      )}
                      {searchQuery && <span className="text-xs text-slate-500">{searchOccurrences.length} found</span>}
                    </div>
                    {searchOccurrences.length > 0 && (
                      <div className={`mt-1 p-2 rounded text-xs ${focusMode ? 'bg-amber-50 text-slate-600' : 'bg-slate-900/50 text-slate-300'} border ${focusMode ? 'border-amber-200' : 'border-slate-700/30'}`}>
                        {searchOccurrences[searchMatchIdx]?.snippet}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Shweebum Scribe - Mobile */}
              <div className="md:hidden mb-4">
                {!showScribe ? (
                  <button
                    onClick={() => setShowScribe(true)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${focusMode ? 'bg-amber-100 border-amber-300 text-slate-700' : 'bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border-emerald-500/30 hover:border-emerald-500/50'}`}
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className={`w-4 h-4 ${focusMode ? 'text-amber-600' : 'text-emerald-400'}`} />
                      <span className={`text-sm font-medium ${focusMode ? 'text-slate-700' : 'text-emerald-300'}`}>Shweebum Scribe</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${focusMode ? 'bg-amber-200 text-amber-700' : 'bg-emerald-500/20 text-emerald-400'}`}>FREE</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-medium ${focusMode ? 'text-slate-700' : 'text-white'}`}>{content.split(/\s+/).filter(w => w).length} words</span>
                      <ChevronLeft className="w-4 h-4 text-slate-400 rotate-[-90deg]" />
                    </div>
                  </button>
                ) : (
                  <div className="relative">
                    <button
                      onClick={() => setShowScribe(false)}
                      className="absolute top-3 right-3 z-10 p-1 rounded bg-slate-800/80 text-slate-400 hover:text-white"
                    >
                      <ChevronLeft className="w-4 h-4 rotate-90" />
                    </button>
                    <ShweebumScribe 
                      content={content}
                      wordCount={content.split(/\s+/).filter(w => w).length}
                      dailyGoal={500}
                      onContentChange={(newContent) => handleContentChange(newContent)}
                      compact={false}
                    />
                  </div>
                )}
              </div>

              {/* Editor textarea */}
              <Textarea
                ref={contentRef}
                value={content}
                onChange={(e) => handleContentChange(e.target.value)}
                onMouseUp={handleTextSelection}
                placeholder="Start writing your story here... Let your imagination flow freely!"
                className={`flex-1 resize-none text-base leading-relaxed min-h-[400px] transition-all duration-500 ${
                  focusMode 
                    ? 'bg-[#F4ECD8] border-amber-200 text-slate-800 placeholder:text-amber-400 font-serif text-lg' 
                    : 'bg-slate-900/30 border-slate-700/50 text-white placeholder:text-slate-500'
                }`}
                data-testid="write-textarea"
              />
              
              {/* Wow Factor message */}
              <AnimatePresence>
                {wowMessage && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`mt-2 px-4 py-2 rounded-lg text-sm flex items-center gap-2 ${
                      focusMode ? 'bg-amber-100 border border-amber-300 text-amber-800' : 'bg-cyan-950/60 border border-cyan-500/30 text-cyan-300'
                    }`}
                    data-testid="wow-message"
                  >
                    <Sparkles className="w-4 h-4 shrink-0" />
                    {wowMessage}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Overused Words strip */}
              {overusedWords.length > 0 && (
                <div className={`mt-2 flex items-center gap-2 flex-wrap p-2 rounded-lg ${focusMode ? 'bg-amber-100' : 'bg-slate-900/40'}`}>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider shrink-0 ${focusMode ? 'text-amber-700' : 'text-rose-400'}`}>Overused:</span>
                  {overusedWords.slice(0, 5).map(({ word, count }, idx) => (
                    <button
                      key={word}
                      onClick={() => {
                        setOverusedIdx(idx);
                        handleSearch(word);
                        setShowSearch(true);
                      }}
                      className={`text-[11px] px-2 py-0.5 rounded-full transition-all hover:scale-105 ${
                        focusMode ? 'bg-amber-200 text-amber-800 hover:bg-amber-300' : 'bg-rose-900/40 text-rose-300 border border-rose-500/30 hover:bg-rose-900/60'
                      }`}
                      data-testid={`overused-word-${word}`}
                    >
                      {word} ×{count}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Quick actions */}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleQuickVoice}
                  className={focusMode ? "border-amber-300 text-amber-700 hover:bg-amber-100" : "border-slate-600 text-slate-300"}
                >
                  {isPlaying ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  {isPlaying ? "Stop" : "Listen"}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleExport('txt')}
                  className={focusMode ? "border-amber-300 text-amber-700 hover:bg-amber-100" : "border-slate-600 text-slate-300"}
                >
                  <Download className="w-4 h-4 mr-1" />
                  Export TXT
                </Button>
                <span className={`text-xs ml-auto ${focusMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  {content.split(/\s+/).filter(w => w).length} words
                </span>
              </div>
            </div>
          )}

          {/* LIVE INSIGHTS */}
          {activeTool === "insights" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-teal-400" />
                  Live Writing Insights
                  <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30 text-[10px]">LIVE</Badge>
                </h2>
                <p className="text-sm text-slate-400 mb-4">Real-time stats about your writing. No button needed — just write!</p>
                
                {content.length < 50 ? (
                  <div className="text-center py-12 text-slate-500">
                    <BarChart3 className="w-10 h-10 mx-auto mb-3 opacity-30" />
                    <p>Write at least a few sentences to see insights.</p>
                  </div>
                ) : (() => {
                  const words = content.split(/\s+/).filter(w => w.length > 0);
                  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 5);
                  const paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 0);
                  const dialogueMatches = content.match(/[""][^""]+[""]/g) || [];
                  const dialogueWords = dialogueMatches.join(' ').split(/\s+/).length;
                  const dialoguePct = words.length > 0 ? Math.round((dialogueWords / words.length) * 100) : 0;
                  const avgSentLen = sentences.length > 0 ? Math.round(words.length / sentences.length) : 0;
                  const longestSentence = sentences.reduce((a, b) => a.split(/\s+/).length > b.split(/\s+/).length ? a : b, '');
                  const longestLen = longestSentence.split(/\s+/).length;
                  
                  return (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-teal-500/20">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Words</p>
                          <p className="text-2xl font-bold text-white">{words.length.toLocaleString()}</p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-teal-500/20">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Sentences</p>
                          <p className="text-2xl font-bold text-white">{sentences.length}</p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-teal-500/20">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Avg Sentence Length</p>
                          <p className="text-2xl font-bold text-white">{avgSentLen} <span className="text-sm text-slate-400">words</span></p>
                          <p className="text-[10px] text-teal-400 mt-1">{avgSentLen < 12 ? "Short & punchy" : avgSentLen < 20 ? "Balanced" : "Long & flowing"}</p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-teal-500/20">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider">Dialogue</p>
                          <p className="text-2xl font-bold text-white">{dialoguePct}%</p>
                          <p className="text-[10px] text-teal-400 mt-1">{dialoguePct > 30 ? "Very dialogue-heavy" : dialoguePct > 15 ? "Good mix" : "Mostly narrative"}</p>
                        </div>
                        <div className="bg-slate-900/50 rounded-lg p-3 border border-teal-500/20 col-span-2">
                          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Longest Sentence ({longestLen} words)</p>
                          <p className="text-xs text-slate-300 italic line-clamp-2">"{longestSentence.trim().slice(0, 120)}..."</p>
                        </div>
                      </div>
                      
                      {/* Overused Words in Insights */}
                      {overusedWords.length > 0 && (
                        <div className="bg-rose-950/30 rounded-lg p-3 border border-rose-500/20">
                          <p className="text-[10px] text-rose-400 uppercase tracking-wider mb-2">Overused Words</p>
                          <div className="flex flex-wrap gap-2">
                            {overusedWords.map(({ word, count }) => (
                              <button
                                key={word}
                                onClick={() => { setActiveTool("search"); handleSearch(word); }}
                                className="text-xs px-2 py-1 bg-rose-900/40 text-rose-300 rounded-full border border-rose-500/20 hover:bg-rose-900/60"
                                data-testid={`insight-overused-${word}`}
                              >
                                {word} ×{count}
                              </button>
                            ))}
                          </div>
                          <p className="text-[10px] text-slate-500 mt-2">Click a word to search for it.</p>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* SMART SEARCH */}
          {activeTool === "search" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Search className="w-5 h-5 text-blue-400" />
                  Smart Search
                </h2>
                <p className="text-sm text-slate-400 mb-4">Find any word or phrase in your manuscript.</p>
                
                <div className="flex gap-2 mb-4">
                  <Input
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder="Search in your story..."
                    className="bg-slate-800/50 border-slate-700 text-white"
                    data-testid="smart-search-input"
                  />
                  {searchQuery && (
                    <Button variant="outline" size="sm" onClick={() => { setSearchQuery(''); setSearchOccurrences([]); }} className="border-slate-600 text-slate-400">
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                
                {searchQuery && (
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-sm text-slate-400">{searchOccurrences.length} occurrence{searchOccurrences.length !== 1 ? 's' : ''} found</span>
                    {searchOccurrences.length > 0 && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSearchMatchIdx(i => Math.max(0, i - 1))} className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800"><ChevronUp className="w-4 h-4" /></button>
                        <span className="text-xs text-cyan-400">{searchMatchIdx + 1}/{searchOccurrences.length}</span>
                        <button onClick={() => setSearchMatchIdx(i => Math.min(searchOccurrences.length - 1, i + 1))} className="p-1 rounded text-slate-400 hover:text-white hover:bg-slate-800"><ChevronDown className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  {searchOccurrences.map((match, idx) => (
                    <div
                      key={idx}
                      onClick={() => setSearchMatchIdx(idx)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        idx === searchMatchIdx 
                          ? 'bg-blue-900/30 border-blue-500/50' 
                          : 'bg-slate-900/40 border-slate-700/40 hover:border-slate-600/60'
                      }`}
                    >
                      <p className="text-xs text-slate-400 mb-1">Match {idx + 1}</p>
                      <p className="text-sm text-slate-200"
                        dangerouslySetInnerHTML={{ __html: match.snippet.replace(new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), `<mark class="bg-yellow-500/30 text-yellow-200 rounded px-0.5">$&</mark>`) }}
                      />
                    </div>
                  ))}
                  {!searchQuery && (
                    <div className="text-center py-12 text-slate-500">
                      <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
                      <p>Type to search your manuscript</p>
                    </div>
                  )}
                  {searchQuery && searchOccurrences.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <p>No matches found for "{searchQuery}"</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STORY DOCTOR */}
          {activeTool === "doctor" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Stethoscope className="w-5 h-5 text-emerald-400" />
                  Story Doctor
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  Analyze your story for plot holes, pacing issues, and consistency problems.
                </p>
                
                <Button
                  onClick={handleStoryDoctor}
                  disabled={analyzing}
                  className="bg-emerald-600 hover:bg-emerald-500 mb-4"
                >
                  {analyzing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><Stethoscope className="w-4 h-4 mr-2" /> Analyze Story</>
                  )}
                </Button>
                
                {analysisResult && analysisResult.type !== "manuscript" && (
                  <div className="space-y-3">
                    {analysisResult.issues?.map((issue, idx) => (
                      <div key={idx} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`text-[10px] ${
                            issue.severity === 'high' ? 'bg-red-500/20 text-red-400' :
                            issue.severity === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {issue.severity}
                          </Badge>
                          <span className="text-sm font-medium text-white">{issue.type}</span>
                        </div>
                        <p className="text-sm text-slate-300">{issue.description}</p>
                        {issue.suggestion && (
                          <p className="text-xs text-emerald-400 mt-1">💡 {issue.suggestion}</p>
                        )}
                      </div>
                    ))}
                    {(!analysisResult.issues || analysisResult.issues.length === 0) && (
                      <div className="bg-emerald-950/30 rounded-lg p-4 border border-emerald-500/30 text-center">
                        <Check className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <p className="text-emerald-300">No major issues found!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MANUSCRIPT ANALYSIS */}
          {activeTool === "analysis" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-400" />
                  Manuscript Analysis
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  Get readability scores and writing statistics.
                </p>
                
                <Button
                  onClick={handleManuscriptAnalysis}
                  disabled={analyzing}
                  className="bg-amber-600 hover:bg-amber-500 mb-4"
                >
                  {analyzing ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                  ) : (
                    <><BarChart3 className="w-4 h-4 mr-2" /> Analyze Manuscript</>
                  )}
                </Button>
                
                {analysisResult && analysisResult.type === "manuscript" && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <p className="text-xs text-slate-500">Word Count</p>
                      <p className="text-xl font-bold text-white">{analysisResult.wordCount}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <p className="text-xs text-slate-500">Sentences</p>
                      <p className="text-xl font-bold text-white">{analysisResult.sentenceCount}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <p className="text-xs text-slate-500">Avg Words/Sentence</p>
                      <p className="text-xl font-bold text-white">{analysisResult.avgWordsPerSentence}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <p className="text-xs text-slate-500">Readability</p>
                      <p className="text-xl font-bold text-white">{analysisResult.readabilityScore}</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <p className="text-xs text-slate-500">Passive Voice</p>
                      <p className="text-xl font-bold text-white">{analysisResult.passiveVoicePercent}%</p>
                    </div>
                    <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <p className="text-xs text-slate-500">Adverbs</p>
                      <p className="text-xl font-bold text-white">{analysisResult.adverbCount}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* WRITING POLISH */}
          {activeTool === "polish" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-blue-400" />
                  Writing Polish Tools
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  {selectedText ? `Selected: "${selectedText.slice(0, 50)}..."` : "Select text in the Write tab, then come here to polish it."}
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {POLISH_TOOLS.map(tool => (
                    <button
                      key={tool.id}
                      onClick={() => handlePolish(tool)}
                      disabled={polishing}
                      className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50 hover:border-blue-500/50 transition-all text-left"
                    >
                      <p className="text-sm font-medium text-white">{tool.label}</p>
                      <p className="text-xs text-slate-400">{tool.description}</p>
                    </button>
                  ))}
                </div>
                
                {polishing && (
                  <div className="flex items-center gap-2 text-blue-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Polishing with {polishTool?.label}...</span>
                  </div>
                )}
                
                {polishResult && (
                  <div className="bg-slate-900/50 rounded-lg p-4 border border-blue-500/30">
                    <p className="text-xs text-blue-400 mb-2">Polished Result:</p>
                    <p className="text-sm text-slate-200 whitespace-pre-wrap mb-3">{polishResult.polished_text}</p>
                    <Button onClick={applyPolishResult} size="sm" className="bg-blue-600 hover:bg-blue-500">
                      <Check className="w-4 h-4 mr-1" /> Apply Changes
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* QUICK VOICE */}
          {activeTool === "voice" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto text-center">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center justify-center gap-2">
                  <Volume2 className="w-5 h-5 text-teal-400" />
                  Quick Voice
                </h2>
                <p className="text-sm text-slate-400 mb-6">
                  Listen to your writing read aloud using your browser's built-in voice.
                </p>
                
                <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700/50 inline-block">
                  <Button
                    onClick={handleQuickVoice}
                    size="lg"
                    className={isPlaying ? "bg-red-600 hover:bg-red-500" : "bg-teal-600 hover:bg-teal-500"}
                  >
                    {isPlaying ? (
                      <><Square className="w-5 h-5 mr-2" /> Stop</>
                    ) : (
                      <><Play className="w-5 h-5 mr-2" /> Play</>
                    )}
                  </Button>
                  
                  <p className="text-xs text-slate-500 mt-4">
                    {selectedText ? "Playing selected text" : "Playing full content"}
                  </p>
                </div>
                
                <div className="mt-6 p-4 bg-cyan-950/30 rounded-lg border border-cyan-500/20">
                  <p className="text-sm text-cyan-300">
                    💡 Want premium AI voices with emotions and accents?
                  </p>
                  <Link to="/pricing">
                    <Button variant="outline" size="sm" className="mt-2 border-cyan-500/40 text-cyan-400">
                      Upgrade to Plus
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* OUTLINE */}
          {activeTool === "outline" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Map className="w-5 h-5 text-violet-400" />
                  Story Outline
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  Plan your chapters and story structure.
                </p>
                
                <div className="space-y-2">
                  {chapters.map((ch, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-slate-500">Ch {ch.number}</span>
                        <Input
                          value={ch.title}
                          onChange={(e) => {
                            const newChapters = [...chapters];
                            newChapters[idx].title = e.target.value;
                            setChapters(newChapters);
                          }}
                          placeholder="Chapter title..."
                          className="flex-1 bg-transparent border-none text-white text-sm"
                        />
                      </div>
                    </div>
                  ))}
                  
                  {chapters.length < 20 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setChapters([...chapters, { number: chapters.length + 1, title: "" }])}
                      className="w-full border-dashed border-slate-600 text-slate-400"
                    >
                      + Add Chapter
                    </Button>
                  )}
                  
                  {chapters.length >= 20 && (
                    <p className="text-xs text-amber-400 text-center">
                      Freebum limit: 20 chapters. Upgrade for unlimited!
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* CHARACTERS */}
          {activeTool === "characters" && (
            <div className="flex-1 p-4 overflow-y-auto">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-400" />
                  Character Builder
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  Create and develop your characters.
                </p>
                
                <div className="space-y-3">
                  {(project.characters || []).map((char, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-lg p-4 border border-slate-700/50">
                      <p className="font-medium text-white">{char.name}</p>
                      <p className="text-xs text-slate-400 mt-1">{char.role}</p>
                      {char.description && (
                        <p className="text-sm text-slate-300 mt-2">{char.description}</p>
                      )}
                    </div>
                  ))}
                  
                  {(!project.characters || project.characters.length === 0) && (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-slate-600 mx-auto mb-2" />
                      <p className="text-slate-400 text-sm">No characters yet</p>
                      <p className="text-slate-500 text-xs mt-1">Characters created during project setup will appear here</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Shweebum Scribe - Desktop only: Floating panel */}
      {showScribe && activeTool === "write" && (
        <div className="hidden md:block fixed bottom-4 right-4 z-50 w-80">
          <ShweebumScribe 
            content={content}
            wordCount={content.split(/\s+/).filter(w => w).length}
            dailyGoal={500}
            onContentChange={(newContent) => handleContentChange(newContent)}
          />
        </div>
      )}
      
      {/* Scribe Toggle Button - Desktop only (when hidden and in write mode) */}
      {!showScribe && activeTool === "write" && (
        <button
          onClick={() => setShowScribe(true)}
          className="hidden md:flex fixed bottom-4 right-4 z-50 items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg hover:shadow-emerald-500/30 transition-all hover:scale-105"
        >
          <Sparkles className="w-4 h-4" />
          <span className="text-sm font-medium">Scribe</span>
        </button>
      )}

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-[#0A0E17] border-t border-slate-700/50 px-2 py-2 z-30">
        <div className="flex items-center justify-around">
          {FREE_TOOLS.slice(0, 5).map(tool => (
            <button
              key={tool.id}
              onClick={() => setActiveTool(tool.id)}
              className={`flex flex-col items-center p-1.5 ${
                activeTool === tool.id ? 'text-cyan-400' : 'text-slate-500'
              }`}
            >
              <tool.icon className="w-5 h-5" />
              <span className="text-[10px] mt-0.5">{tool.label.split(" ")[0]}</span>
            </button>
          ))}
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center p-1.5 text-slate-500"
          >
            <Menu className="w-5 h-5" />
            <span className="text-[10px] mt-0.5">More</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default FreebumWritingPage;
